import { useStore } from 'vuex'
import Sortable from 'sortablejs'
import { computed, getCurrentInstance, ref, useTemplateRef } from 'vue'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'

export const useSortFields = () => {
  const store = useStore()
  const workflow = computed(() => store.state.workflow.workflow)

  const fieldsRef = useTemplateRef('fieldsRef')
  const subFieldsRef = useTemplateRef('subFieldsRef')
  const sortFieldsMap = ref({})

  const instance = getCurrentInstance()

  const queryScope = () => {
    const el = instance?.proxy?.$el
    return el && typeof el.querySelectorAll === 'function' ? el : document
  }

  const createSortable = (field, endCallback) => {
    return new Sortable(field, {
      draggable: '.sortableRow',
      handle: '.sortHandle',
      animation: 150,
      scroll: true,
      forceFallback: false,
      scrollSensitivity: 100,
      onStart: function () {
        Array.prototype.forEach.call(
          document.querySelectorAll('.v-row-group__header'),
          function (el, index) {
            if (!el.classList.contains('sortableRow') && index !== 0) {
              el.classList.add('sortableRow')
            }
          }
        )
        Array.prototype.forEach.call(
          document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
          function (el, index) {
            if (!el.classList.contains('white-background-on-hover') && index !== 0) {
              el.classList.add('white-background-on-hover')
            }
          }
        )
      },
      onEnd: endCallback
    })
  }

  const moveField = async (schemaId, fromIndex, toIndex) => {
    if (!schemaId) {
      return
    }

    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = newSchemas[schemaIndex].fields[fromIndex]
    newSchemas[schemaIndex].fields.splice(fromIndex, 1)
    newSchemas[schemaIndex].fields.splice(toIndex, 0, field)

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const moveSubField = async (schemaId, fromIndex, toIndex, fieldIndex) => {
    if (!schemaId) {
      return
    }

    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const subFields = newSchemas[schemaIndex].fields[fieldIndex].fields
    const [movedField] = subFields.splice(fromIndex, 1)
    subFields.splice(toIndex, 0, movedField)
    newSchemas[schemaIndex].fields[fieldIndex].fields = subFields

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const initSortable = () => {
    destroySortable()

    if (window.Sortable) {
      window.Sortable.active = null
      window.Sortable.dragged = null
      window.Sortable.ghost = null
      window.Sortable.clone = null
      window.Sortable.from = null
      window.Sortable.lastDownEl = null
    }

    const activeUniqueIds = new Set()

    const scope = queryScope()

    const refFields = !fieldsRef.value ? [] : (Array.isArray(fieldsRef.value) ? fieldsRef.value : [fieldsRef.value])
    const domFields = Array.from(scope.querySelectorAll('[data-depth="1"][data-schema-id]'))
    const fields = refFields.length > 0 ? refFields : domFields

    const refSubFields = !subFieldsRef.value ? [] : (Array.isArray(subFieldsRef.value) ? subFieldsRef.value : [subFieldsRef.value])
    const domSubFields = Array.from(scope.querySelectorAll('[data-depth="2"][data-field-index]'))
    const subFields = refSubFields.length > 0 ? refSubFields : domSubFields

    fields.forEach(($field) => {
      const { eventId, schemaId } = $field.dataset
      const uuid = `${eventId}-${schemaId}`
      activeUniqueIds.add(uuid)

      let sortable = sortFieldsMap.value[uuid]

      if (sortable && !sortable.el?.isConnected) {
        sortable.destroy()
        delete sortFieldsMap.value[uuid]
        sortable = null
      }

      if (!sortable) {
        sortable = createSortable($field, (event) =>
          moveField(schemaId, event.oldIndex, event.newIndex)
        )
        sortFieldsMap.value[uuid] = sortable
      }
    })

    const nowTimeStamp = Date.now()

    subFields.forEach(($field) => {
      const { eventId, schemaId, fieldIndex } = $field.dataset
      const uuid = `${eventId}-${schemaId}-${fieldIndex}-${nowTimeStamp}`
      activeUniqueIds.add(uuid)

      let sortable = sortFieldsMap.value[uuid]

      if (sortable && !sortable.el?.isConnected) {
        sortable.destroy()
        delete sortFieldsMap.value[uuid]
        sortable = null
      }

      if (!sortable) {
        sortable = createSortable($field, (event) =>
          moveSubField(schemaId, event.oldIndex, event.newIndex, parseInt(fieldIndex))
        )
        sortFieldsMap.value[uuid] = sortable
      }
    })

    Object.keys(sortFieldsMap.value).forEach((uuid) => {
      if (!activeUniqueIds.has(uuid)) {
        sortFieldsMap.value[uuid].destroy()
        delete sortFieldsMap.value[uuid]
      }
    })
  }

  const destroySortable = () => {
    Object.keys(sortFieldsMap.value).forEach((uuid) => {
      const sortable = sortFieldsMap.value[uuid]
      if (sortable && typeof sortable.destroy === 'function') {
        sortable.destroy()
      }
      delete sortFieldsMap.value[uuid]
    })

    document.querySelectorAll('.sortable-drag, .sortable-ghost, .sortable-fallback')
      .forEach(el => el.remove())
  }

  return { fieldsRef, subFieldsRef, initSortable, destroySortable, createSortable }
}
